import { privateApi } from 'api'
import { AxiosResponse } from 'axios'
import { ECdekBarcodeFormat, IAdminChangeOrderStatus, IAdminChangeOrderStatusResponse, IAdminDenyItem, IAdminExtendOrderDeadlineRequest, IAdminExtendOrderDeadlineResponse, IAdminGetOrderResponse, IAdminMultiOrderResponse, IAdminOrdersQueryRequest, IAdminPaymentLinkResponse, IAdminOrdersQueryResponse, IAdminUpdateOrderRequest, ICdekRefreshResponse, IMultiOrdersQueryRequest, IMultiOrdersQueryResponse, IShipmentPointChangeResponse, IShipmentPointQuoteResponse } from 'models/orders'
import { getNormalizedFormData } from 'shared/utils/getNormalizedFormData'

export const ordersApi = {
  getOrders({
    skip,
    limit,
    name,
    from,
    to,
    number,
    tel,
    seller_id,
    sum,
    status,
    promo_code_id,
    super_orders,
    user_id
  }: IAdminOrdersQueryRequest): Promise<AxiosResponse<IAdminOrdersQueryResponse>> {
    return privateApi.get<IAdminOrdersQueryResponse>(
      `admin/order?skip=${skip}&limit=${limit}&name=${name ?? ''}&from=${from ?? ''}&to=${to ?? ''}&number=${number ?? ''}&tel=${tel ?? ''}&seller_id=${seller_id ?? ''}&sum=${sum ?? ''}&status=${status ?? ''}&promo_code_id=${promo_code_id ?? ''}&super_orders=${super_orders ?? ''}&user_id=${user_id ?? ''}`
    )
  },
  getOrder(orderId: string): Promise<AxiosResponse<IAdminGetOrderResponse>> {
    return privateApi.get<IAdminGetOrderResponse>(`admin/order/${orderId}`)
  },
  changeStatus(data: IAdminChangeOrderStatus): Promise<AxiosResponse<IAdminChangeOrderStatusResponse>> {
    return privateApi.patch<IAdminChangeOrderStatusResponse>('admin/order/status', data)
  },
  extendDeadline(
    orderId: string,
    data: IAdminExtendOrderDeadlineRequest
  ): Promise<AxiosResponse<IAdminExtendOrderDeadlineResponse>> {
    return privateApi.patch<IAdminExtendOrderDeadlineResponse>(`admin/order/deadline/${orderId}`, data)
  },
  removeProduct(orderId: string, data: IAdminDenyItem): Promise<AxiosResponse<IAdminGetOrderResponse>> {
    return privateApi.post<IAdminGetOrderResponse>(`admin/order/items/deny/${orderId}`, data)
  },
  updateOrder(orderId: string, data: IAdminUpdateOrderRequest): Promise<AxiosResponse<IAdminGetOrderResponse>> {
    const formData = getNormalizedFormData(data)
    return privateApi.patch<IAdminGetOrderResponse>(`admin/order/edit/${orderId}`, formData)
  },
  getSuperOrder(orderId: string): Promise<AxiosResponse<IAdminMultiOrderResponse>> {
    return privateApi.get<IAdminMultiOrderResponse>(`admin/multiOrder/${orderId}`)
  },
  downloadInvoice(multiOrderId: string): Promise<AxiosResponse<Blob>> {
    return privateApi.get<Blob>(
      `admin/multiOrder/byId/${multiOrderId}/invoice`,
      { responseType: 'blob' }
    )
  },
  confirmInvoicePayment(multiOrderId: string): Promise<AxiosResponse<unknown>> {
    return privateApi.post(`admin/multiOrder/byId/${multiOrderId}/invoice-paid`)
  },
  cancelUnpaidSuperOrder(multiOrderId: string): Promise<AxiosResponse<unknown>> {
    return privateApi.post(`admin/multiOrder/byId/${multiOrderId}/cancel-unpaid`)
  },
  getSuperOrders({
    skip,
    limit,
    name,
    from,
    to,
    number,
    tel,
    sum,
    user_id
  }: IMultiOrdersQueryRequest): Promise<AxiosResponse<IMultiOrdersQueryResponse>> {
    return privateApi.get<IMultiOrdersQueryResponse>(
      `admin/multiOrder/list?skip=${skip}&limit=${limit}&name=${name ?? ''}&from=${from ?? ''}&to=${to ?? ''}&number=${number ?? ''}&tel=${tel ?? ''}&sum=${sum ?? ''}&user_id=${user_id ?? ''}`
    )
  },
  exportOrdersReport(params: { dateFrom?: string; dateTo?: string; seller_id?: string }): Promise<AxiosResponse<Blob>> {
    const { dateFrom, dateTo, seller_id } = params
    return privateApi.get<Blob>(
      `export/report/orders?dateFrom=${dateFrom ?? ''}&dateTo=${dateTo ?? ''}&seller_id=${seller_id ?? ''}`,
      { responseType: 'blob' }
    )
  },
  refreshCdekStatus(orderId: string): Promise<AxiosResponse<ICdekRefreshResponse>> {
    return privateApi.post<ICdekRefreshResponse>(`seller/orders/${orderId}/cdek-refresh`)
  },
  getDeliveryLabel(orderId: string, format: ECdekBarcodeFormat): Promise<AxiosResponse<Blob>> {
    return privateApi.get<Blob>(
      `seller/orders/${orderId}/delivery-label?format=${format}`,
      { responseType: 'blob' }
    )
  },
  quoteShipmentPoint(orderId: string, code: string): Promise<AxiosResponse<IShipmentPointQuoteResponse>> {
    return privateApi.post<IShipmentPointQuoteResponse>(`seller/orders/${orderId}/shipment-point/quote`, { code })
  },
  changeShipmentPoint(orderId: string, code: string): Promise<AxiosResponse<IShipmentPointChangeResponse>> {
    return privateApi.patch<IShipmentPointChangeResponse>(`seller/orders/${orderId}/shipment-point`, { code })
  },
  getPaymentLink(orderId: string): Promise<AxiosResponse<IAdminPaymentLinkResponse>> {
    return privateApi.get<IAdminPaymentLinkResponse>(`admin/order/payment-link/${orderId}`)
  },
  renewPaymentLink(orderId: string): Promise<AxiosResponse<IAdminPaymentLinkResponse>> {
    return privateApi.post<IAdminPaymentLinkResponse>(`admin/order/payment-link/${orderId}`)
  }
}
